// src/pages/public/Home.js
import React from "react";
import { Helmet } from 'react-helmet-async';
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { COMPANY, PROJECTS, TESTIMONIALS, SHOP_SERVICES } from "../../utils/constants";

const fadeUp = { hidden: { opacity: 0, y: 30 }, show: { opacity: 1, y: 0 } };
const stagger = { show: { transition: { staggerChildren: 0.1 } } };

export default function Home() {
  return (
    <div>
      <Helmet>
        <title>Aimble I.T. Solutions — Website Developer in Irosin, Sorsogon</title>
        <meta name="description" content="Professional website developer in Irosin, Sorsogon. Aimble I.T. Solutions builds custom websites, integrates Maya & GCash payments, and offers virtual assistant services. BIR & DTI registered." />
        <link rel="canonical" href="https://www.aimbletech.com/" />
      </Helmet>
      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section style={{ position: "relative", minHeight: "100vh", display: "flex", alignItems: "center", paddingTop: "var(--header-h)", background: "var(--navy)" }}>
        
        {/* Professional Subtle Grid Background (No glowing orbs) */}
        <div style={{
          position: "absolute", inset: 0, zIndex: 0, opacity: 0.03,
          backgroundImage: `linear-gradient(var(--text) 1px, transparent 1px), linear-gradient(90deg, var(--text) 1px, transparent 1px)`,
          backgroundSize: "64px 64px",
          maskImage: "linear-gradient(to bottom, black 40%, transparent 100%)",
          WebkitMaskImage: "linear-gradient(to bottom, black 40%, transparent 100%)"
        }} />

        <div className="container" style={{ position: "relative", zIndex: 1 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: "64px", alignItems: "center" }}>
            
            {/* Left Column: Copy & CTAs */}
            <motion.div initial="hidden" animate="show" variants={stagger}>
              <motion.div variants={fadeUp} className="badge badge-accent" style={{ marginBottom: 24, display: "inline-block" }}>
                ✓ Bicol's Premium IT Partner
              </motion.div>
              
              <motion.h1 variants={fadeUp} className="display" style={{ marginBottom: 24, lineHeight: 1.1 }}>
                Build fast. <br />
                <span style={{ color: "var(--accent)" }}>Scale securely.</span>
              </motion.h1>
              
              <motion.p variants={fadeUp} className="body-lg text-muted" style={{ marginBottom: 40, maxWidth: 540 }}>
                We provide comprehensive web development and IT solutions tailored for businesses in Irosin, Sorsogon, and beyond. Let's modernize your operations.
              </motion.p>
              
              <motion.div variants={fadeUp} className="flex-row gap-md flex-wrap">
                <Link to="/packages" className="btn btn-primary btn-lg">Explore Packages</Link>
                <Link to="/contact" className="btn btn-secondary btn-lg">Book a Consultation</Link>
              </motion.div>
            </motion.div>

            {/* Right Column: Trust Indicators / Clean Graphic */}
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2, duration: 0.5 }}>
              <div className="card" style={{ padding: "40px", background: "linear-gradient(145deg, var(--surface) 0%, var(--surface-2) 100%)", border: "1px solid var(--border-med)" }}>
                <h3 style={{ fontSize: "1.25rem", fontWeight: 600, marginBottom: 24 }}>Why choose Aimble?</h3>
                <div className="flex-col gap-lg">
                  <div className="flex-row gap-md">
                    <div style={{ color: "var(--accent)", fontSize: 24 }}>⚡</div>
                    <div>
                      <div style={{ fontWeight: 600 }}>Lightning Fast Delivery</div>
                      <div style={{ fontSize: "0.875rem", color: "var(--text-muted)" }}>Modern tech stack optimized for speed.</div>
                    </div>
                  </div>
                  <div className="flex-row gap-md">
                    <div style={{ color: "var(--accent)", fontSize: 24 }}>🔒</div>
                    <div>
                      <div style={{ fontWeight: 600 }}>Bank-Grade Security</div>
                      <div style={{ fontSize: "0.875rem", color: "var(--text-muted)" }}>Secure authentication and databases.</div>
                    </div>
                  </div>
                  <div className="flex-row gap-md">
                    <div style={{ color: "var(--accent)", fontSize: 24 }}>🧾</div>
                    <div>
                      <div style={{ fontWeight: 600 }}>Fully Registered</div>
                      <div style={{ fontSize: "0.875rem", color: "var(--text-muted)" }}>BIR & DTI registered. Official receipts provided.</div>
                      <img 
                        src="/BIR_BADGE.png" 
                        alt="BIR Registered Business" 
                        style={{ height: 150, width: 1000, objectFit: "contain", filter: "drop-shadow(0 4px 6px rgba(0,0,0,0.1))" }} 
                      />
                    </div>
                  </div>
                </div>
              </div>
              
            </motion.div>

          </div>
        </div>
        
        {/* Mobile responsiveness for the grid */}
        <style>{`@media(max-width: 900px){ .container > div { grid-template-columns: 1fr !important; gap: 48px !important; } }`}</style>
      </section>

      {/* ── SERVICES OVERVIEW ──────────────────────────────────────────────── */}
      <section className="section" style={{ background: "var(--surface)" }}>
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ marginBottom: 48 }}>
            <div className="section-label">What We Offer</div>
            <h2 className="heading-1" style={{ maxWidth: 500 }}>One team. Many capabilities.</h2>
          </motion.div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 24 }}>
            {[
              { icon: "🌐", title: "Web Development", desc: "Custom websites and web apps built on Firebase. Fast, secure, SEO-friendly.", link: "/services", badge: "Core Service" },
              { icon: "💳", title: "Payment Solutions", desc: "Integrate Maya, GCash, and credit card payments. We process on your behalf.", link: "/services", badge: "Maya Ready" },
              { icon: "🤝", title: "Virtual Assistant", desc: "Dedicated VAs for customer support, data entry, email and calendar management.", link: "/services", badge: "Monthly Plans" },
              { icon: "🖥️", title: "PC Rental & Services", desc: "Computer rental, printing, photocopy, photo development, and tech merchandise.", link: "/services", badge: "Walk-In" },
            ].map(({ icon, title, desc, link, badge }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
              >
                <Link to={link} style={{ display: "block", height: "100%" }}>
                  <div className="card card-glow" style={{ height: "100%", cursor: "pointer" }}>
                    <div style={{ fontSize: 32, marginBottom: 16 }}>{icon}</div>
                    <span className="badge badge-accent" style={{ marginBottom: 12 }}>{badge}</span>
                    <h3 className="heading-3" style={{ marginBottom: 8 }}>{title}</h3>
                    <p style={{ fontSize: "0.875rem", color: "var(--text-muted)", lineHeight: 1.6 }}>{desc}</p>
                    <div style={{ marginTop: 16, fontSize: "0.875rem", color: "var(--accent)", fontWeight: 500 }}>Learn more →</div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PORTFOLIO PREVIEW ──────────────────────────────────────────────── */}
      <section className="section">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 48, flexWrap: "wrap", gap: 16 }}>
            <div>
              <div className="section-label">Our Work</div>
              <h2 className="heading-1">Recent projects</h2>
            </div>
            <Link to="/portfolio" className="btn btn-secondary btn-sm">View All →</Link>
          </motion.div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 24 }}>
            {PROJECTS.slice(0, 3).map((project, i) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <a href={project.link !== "#" ? project.link : undefined} target="_blank" rel="noopener noreferrer" style={{ display: "block" }}>
                  <div className="card card-glow" style={{ cursor: project.link !== "#" ? "pointer" : "default" }}>
                    {/* Project preview area */}
                    <div style={{
                      height: 160,
                      background: `linear-gradient(135deg, var(--navy-mid), var(--navy-light))`,
                      borderRadius: 8,
                      marginBottom: 16,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                      overflow: "hidden",
                    }}>
                      {project.image ? (
                        <img src={project.image} alt={project.title} style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8 }} />
                      ) : (
                        <span style={{ fontSize: "3rem", opacity: 0.3 }}>🌐</span>
                      )}
                      {project.link !== "#" && (
                        <div style={{ position: "absolute", top: 8, right: 8 }}>
                          <span className="badge badge-accent">Live ↗</span>
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
                      {project.tags.map(t => <span key={t} className="badge badge-blue">{t}</span>)}
                    </div>
                    <h3 className="heading-3" style={{ marginBottom: 6 }}>{project.title}</h3>
                    <p style={{ fontSize: "0.875rem", color: "var(--text-muted)" }}>{project.description}</p>
                  </div>
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SHOP SERVICES STRIP ────────────────────────────────────────────── */}
      <section className="section-sm" style={{ background: "var(--surface)", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }}>
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ textAlign: "center", marginBottom: 40 }}>
            <div className="section-label" style={{ justifyContent: "center" }}>Visit Our Shop</div>
            <h2 className="heading-2">Also available in-store in Irosin, Sorsogon</h2>
          </motion.div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 16 }}>
            {SHOP_SERVICES.map((s, i) => (
              <motion.div key={s.id} initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
                <div style={{ textAlign: "center", padding: "20px 16px", background: "var(--navy-mid)", borderRadius: 12, border: "1px solid var(--border)" }}>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>{s.icon}</div>
                  <div style={{ fontSize: "0.8125rem", fontWeight: 600, color: "var(--text)", marginBottom: 4 }}>{s.name}</div>
                  <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{s.desc.split(".")[0]}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ──────────────────────────────────────────────────── */}
      <section className="section">
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} style={{ textAlign: "center", marginBottom: 48 }}>
            <div className="section-label" style={{ justifyContent: "center" }}>Testimonials</div>
            <h2 className="heading-1">What clients say</h2>
          </motion.div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 24 }}>
            {TESTIMONIALS.map((t, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <div className="card" style={{ borderColor: "var(--border-med)" }}>
                  <div style={{ fontSize: "1.5rem", marginBottom: 16, color: "var(--accent)" }}>"</div>
                  <p style={{ fontSize: "0.9375rem", color: "var(--text-muted)", lineHeight: 1.7, marginBottom: 20, fontStyle: "italic" }}>{t.quote}</p>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 36, height: 36, borderRadius: "50%", background: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, color: "var(--navy)", fontFamily: "var(--font-head)" }}>
                      {t.name[0]}
                    </div>
                    <div>
                      <div style={{ fontSize: "0.875rem", fontWeight: 600, color: "var(--text)" }}>{t.name}</div>
                      <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{t.role}</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ────────────────────────────────────────────────────── */}
      <section className="section-sm">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            style={{
              background: "linear-gradient(135deg, var(--navy-light) 0%, var(--surface-2) 100%)",
              border: "1px solid var(--border-med)",
              borderRadius: 24,
              padding: "56px 48px",
              textAlign: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div style={{ position: "absolute", top: -40, right: -40, width: 200, height: 200, background: "radial-gradient(circle, rgba(0,201,167,0.12) 0%, transparent 70%)", borderRadius: "50%", pointerEvents: "none" }} />
            <h2 className="heading-1" style={{ marginBottom: 16, position: "relative" }}>
              Ready to start your project?
            </h2>
            <p className="body-lg text-muted" style={{ marginBottom: 32, maxWidth: 480, margin: "0 auto 32px", position: "relative" }}>
              Get a free consultation. We'll help you choose the right package and build something great together.
            </p>
            <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap", position: "relative" }}>
              <Link to="/packages" className="btn btn-primary btn-lg">View Packages</Link>
              <a href={COMPANY.googleCalendar} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-lg">Book Appointment</a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
